import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';
import { ErrorCode } from '../gen/graphql';
import { ErrorMessage } from '../const/ErrorMessage';

// APIエラー時に表示するメッセージ共通処理
// NOTE: 個別処理が必要な場合はどう実装するか要検討
export const getApiErrorMessage = (e: unknown, defaultErrorMessage?: string): string => {
  if (e instanceof ApolloError) {
    const errorCodes = getGraphQLErrorCodes(e);
    if (errorCodes.includes(ErrorCode.CommonUserIsDenied)) {
      return 'アカウント情報が更新されたため、ログインし直してください。';
    }
    if (errorCodes.includes(ErrorCode.StripeDoNotHonorErr)) {
      // stripe側でも拒否理由がわからないため本人からカード発行会社への問い合わせが必要となるケース
      return 'クレジットカードによる支払いが拒否されました。時間をおいてお試しいただくか、カード発行会社にお問い合わせください。';
    }
    if (errorCodes.includes(ErrorCode.SubscriptionPaymentMethodIsEmpty)) {
      return '支払い方法が設定されていません';
    }
    if (errorCodes.includes(ErrorCode.TeamInvitationReachedMaximumLimit)) {
      return 'チームメンバーよりも契約申し込み人数が少ないです';
    }
    if (errorCodes.includes(ErrorCode.NotPermittedDownNumberOfTeamMembers)) {
      return '銀行振込の場合、利用人数を減らすことはできません。';
    }
    if (errorCodes.includes(ErrorCode.ProgramSatisfactionNotAllCompletedYetErr)) {
      return '全章修了していないため、評価を投稿できませんでした。';
    }
    if (errorCodes.includes(ErrorCode.PracticeSubmitDetailReviewStatusIsInvalid)) {
      return '提出物のステータスが更新されているため、保存できません。最新の提出物を確認してください。';
    }
    if (errorCodes.includes(ErrorCode.StripePromotionCodeAlreadyUsedErr)) {
      return 'ご利用いただいたクーポンは初回契約のみ有効なクーポンです。';
    }
    if (errorCodes.includes(ErrorCode.PromotionCodeIsUsed)) {
      return 'ご利用いただいたクーポンは使用済みです。';
    }
    if (errorCodes.includes(ErrorCode.SubscriptionIsExisted)) {
      return 'すでにサブスクリプション申し込み済みです';
    }
    if (errorCodes.includes(ErrorCode.SubscriptionIsMaintained)) {
      return '決済サービスのメンテナンス中です。時間を空けて再度お試しください';
    }
    if (errorCodes.includes(ErrorCode.StripeCardDeclined)) {
      return '選択したクレジットカードは使用できません。';
    }
    if (errorCodes.includes(ErrorCode.StripeCardExpired)) {
      return 'カードの有効期限が切れています。';
    }
    if (errorCodes.includes(ErrorCode.StripeProcessingError)) {
      return '処理に失敗しました。時間をおいてもう一度クレジットカードの登録を行ってください。';
    }
    if (errorCodes.includes(ErrorCode.ChatMessageAlreadyDeletedError)) {
      return 'メッセージはすでに削除済みの可能性があります。リロード後に再度お試しください。';
    }
    if (errorCodes.includes(ErrorCode.SessionAlreadyReserved)) {
      return '指定された日時は既にセッションの予定があるため登録できません。';
    }
    if (errorCodes.includes(ErrorCode.SessionIsDuringNewYearHolidayPeriod)) {
      return '年末年始の休業期間中(12/30〜1/3)はセッションの登録はできません。';
    }
    if (errorCodes.includes(ErrorCode.SessionIsOverCount)) {
      return 'セッション数の上限を超えています。';
    }
    if (errorCodes.includes(ErrorCode.SpotLessonAlreadyDoneOrCanceled)) {
      return '既に実施済みもしくはキャンセル済みです。';
    }
    if (errorCodes.includes(ErrorCode.SpotLessonIsStarted)) {
      return '開始時刻を過ぎているため、キャンセルできません。';
    }
    if (errorCodes.includes(ErrorCode.StripeCardIncorrectCvc)) {
      return 'カードのセキュリティーコードが間違っています。';
    }
    if (errorCodes.includes(ErrorCode.ProgramSatisfactionAlreadyExistsErr)) {
      return 'この教材の評価を投稿済みです。';
    }
  }
  return defaultErrorMessage ?? ErrorMessage;
};

/**
 * ApolloErrorからエラーコードを取り出す
 * @param ae ApolloError
 * @return エラーコードの配列
 */
export const getGraphQLErrorCodes = (ae: ApolloError): ErrorCode[] => {
  return ae.graphQLErrors
    .map((value: GraphQLError) => {
      return value.extensions.code;
    })
    .filter((errorCode): errorCode is ErrorCode => errorCode !== undefined);
};

/**
 * graphql1のエラーコードからエラーメッセージを取り出す
 * @param e 例外
 * @return エラーメッセージ
 */
export const getGraphQLErrorMessage = (e: unknown): string => {
  if (!(e instanceof ApolloError)) {
    return 'システムエラーで処理に失敗しました。お困りの場合はユーザーサポートまでお問い合わせお願いたします。';
  }
  const errorCodes = getGraphQLErrorCodes(e);
  if (errorCodes.includes(ErrorCode.UserAlreadyExists)) {
    return '会員の登録に失敗しました。すでに登録済のユーザーは登録いただけません。';
  }
  if (errorCodes.includes(ErrorCode.UserAlreadyDeleted)) {
    return '会員の登録に失敗しました。以前削除されたアカウントと同じメールアドレスを使用して、新たなユーザー登録を行うことはできません。';
  }
  if (errorCodes.includes(ErrorCode.CommonUserIsDenied)) {
    return 'アカウント情報が更新されたため、ログインし直してください。';
  }
  if (errorCodes.includes(ErrorCode.PromotionCodeIsUsed)) {
    return 'ご利用いただいたクーポンは使用済みです。';
  }
  if (errorCodes.includes(ErrorCode.TeamMemberIsLastAdmin)) {
    return 'チーム最後の管理者なのでアカウントを削除できません。';
  }
  if (errorCodes.includes(ErrorCode.StudyReportMaxTimeError)) {
    return '1日の合計学習時間が24時間を越えています。';
  }
  if (errorCodes.includes(ErrorCode.UserEmailInvalid)) {
    return 'メールアドレスは正しい形式で入力してください。';
  }
  // チーム系のエラーメッセージ
  if (errorCodes.includes(ErrorCode.TeamInvitationNoValidEmail)) {
    return 'すでにチームに所属済みのユーザーには送信できません。';
  }
  return 'システムエラーで処理に失敗しました。お困りの場合はユーザーサポートまでお問い合わせお願いたします。';
};

export const isErrorCode = (code: unknown): code is ErrorCode => {
  return Object.values(ErrorCode).some((v) => v === code);
};
